import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "../api";
import type {
  VerificationStatusOrNull,
  VerificationStatusResponse,
} from "../api-types";
import { ShowToast } from "../../components/toast";

const STATUS_POLL_INTERVAL_MS = 2500;

const TERMINAL_OVERALL_STATUSES: ReadonlyArray<string> = ["passed", "failed"];

/**
 * Shape of an axios error from the tag-verification endpoints. The backend
 * surfaces four distinct `409` codes; the hook inspects `code` rather than
 * relying on the HTTP status alone. `run_already_active` carries an adopted
 * `runId` so the FE can switch its status query over to the live run instead
 * of starting a duplicate; the other three mean this customer can't be
 * verified right now and remove the step. Every other failure (throttle,
 * 4xx, 5xx, network) is deliberately silent here — the screen's single
 * friendly fail state is the only error surface.
 */
interface VerificationErrorPayload {
  message?: string;
  code?:
    | "phase1_not_ready"
    | "pms_not_set"
    | "hotel_website_not_set"
    | "run_already_active";
  runId?: string;
  [key: string]: unknown;
}

interface VerificationAxiosError {
  response?: {
    status?: number;
    data?: VerificationErrorPayload;
  };
  message?: string;
}

function readErrorShape(error: unknown): VerificationAxiosError {
  if (typeof error === "object" && error !== null) {
    return error as VerificationAxiosError;
  }
  return {};
}

interface UseTagVerificationRunOptions {
  /**
   * Called when the backend rejects the run with 409
   * `code: "run_already_active"`. The caller receives the adopted runId
   * (from `error.response.data.runId`) and is expected to switch its
   * status query over to it. The hook itself does not toast in this case
   * because the recovery is invisible.
   */
  onRunAlreadyActive?: (runId: string) => void;
  /**
   * Called when the backend rejects the run with a 409 code meaning this
   * customer can't be verified right now (`phase1_not_ready`,
   * `pms_not_set`, `hotel_website_not_set`). The verification step is
   * removed for these customers — the caller advances the flow silently
   * instead of showing an error they can't fix. No toast is surfaced.
   */
  onCannotVerify?: (code: string) => void;
}

/**
 * Starts a tag-verification run for the current customer.
 *
 * The hook does not own a `runId` — the caller stashes the returned
 * `{ runId }` and feeds it into `useTagVerificationStatus` to drive the
 * polling display.
 *
 * Error UX is differentiated by the backend's `code` field, not just the
 * HTTP status; see `VerificationErrorPayload` for the four 409 variants.
 */
export function useTagVerificationRun(
  userId: string | undefined,
  options: UseTagVerificationRunOptions = {},
) {
  const { onRunAlreadyActive, onCannotVerify } = options;

  return useMutation<{ runId: string }, unknown, void>({
    mutationFn: () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return api.tagVerificationRun(userId);
    },
    onError: (error: unknown) => {
      const { response } = readErrorShape(error);
      const status = response?.status;
      const data = response?.data;
      const code = data?.code;

      if (status === 409) {
        if (code === "run_already_active") {
          // Invisible recovery: hand the live runId to the caller and
          // suppress the toast — the user should see the existing run's
          // progress, not an error.
          if (data?.runId && onRunAlreadyActive) {
            onRunAlreadyActive(data.runId);
          }
          return;
        }
        if (
          code === "phase1_not_ready" ||
          code === "pms_not_set" ||
          code === "hotel_website_not_set"
        ) {
          // This customer can't be verified right now — the caller removes
          // the verification step for them (review decision: most customers
          // can't troubleshoot these, so they just get to move on).
          onCannotVerify?.(code);
          return;
        }
      }

      // Every other failure (throttle, 4xx, 5xx, network) surfaces through
      // the caller's single friendly fail state — no toast. The caller
      // observes `isError` on the mutation.
    },
  });
}

/**
 * Polls `GET /tag-verification/:userId/status?runId=...` for the live run.
 *
 * Keys on both `userId` and `runId` so a runId rollover (the second-tab /
 * 410 recovery case) invalidates the cache cleanly. Treats `null` data as
 * still-running and only stops on a real terminal `overallStatus`.
 *
 * Retry config: 410 (Gone) surfaces immediately as an error so the caller
 * can clear the stale runId and re-fire the run mutation. All other
 * failures get two retries before surfacing.
 */
export function useTagVerificationStatus(
  userId: string | undefined,
  runId: string | undefined,
) {
  return useQuery<VerificationStatusOrNull>({
    queryKey: ["tag-verification-status", userId, runId],
    queryFn: () => {
      if (!userId || !runId) {
        throw new Error("User ID and runId are required");
      }
      return api.tagVerificationStatus(userId, runId);
    },
    enabled: !!userId && !!runId,
    refetchInterval: (data) =>
      data && TERMINAL_OVERALL_STATUSES.includes(data.overallStatus)
        ? false
        : STATUS_POLL_INTERVAL_MS,
    retry: (failureCount, error) => {
      const status = readErrorShape(error).response?.status;
      if (status === 410) return false;
      return failureCount < 2;
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

/**
 * Records that the customer continued past verification without a pass —
 * marks the run `skippedByCustomer: true` so `determineStepV2` routes past
 * the verify gate on the next load. Not throttled; the only failure UX is a
 * generic toast carrying the backend's message verbatim.
 */
export function useTagVerificationSkip(userId: string | undefined) {
  return useMutation<VerificationStatusResponse, unknown, void>({
    mutationFn: () => {
      if (!userId) {
        throw new Error("User ID is required");
      }
      return api.tagVerificationSkip(userId);
    },
    onError: (error: unknown) => {
      const backendMessage = readErrorShape(error).response?.data?.message;
      const fallback =
        backendMessage ??
        "We couldn't skip verification. Please try again.";
      ShowToast({ type: "error", message: fallback });
    },
  });
}
